import {
   AddAnotherGoFResult,
   ApplicationTemplateProperties,
   ApplicationTypeEnum,
   FieldResponse,
   ShortFallFieldsConfig,
   SystemFieldEnumResponseResult
} from '@flowwai-frontend/bps-officer-web/graphql'
import {
   ApplicationModel,
   ApplicationStore,
   ApplicationTemplateModel,
   ApplicationTemplateStore,
   ApplicationTemplateType,
   FieldsStore,
   GOFResponse,
   RecordUtils,
   Stage,
   StageStore,
   SystemFieldEnum,
   TemplateApplicationTypeEnum,
   TemplateDetailsStatusEnum,
   UnionFieldModelType,
   UserStore
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import { Common_ApplicationTemplate_FragmentFragment } from '../fragments/pipelineItemTemplateFragment.generatedTypes'
import { getTransformedFieldResponse } from '../utils/FieldResponseUtils'
import { convertToGoFWithFieldsType } from '../utils/TransformationUtils/TransformationUtils'
import { SurveyNumberFieldConfig_FragmentFragment } from '../fragments/SurveyNumberFieldConfig.generatedTypes'

import { convertGraphqlFieldResponseToUnionFieldResponse } from './fields'

interface StoreApplicationFieldResponsesInApplicationModelProps {
   fieldResponse: SystemFieldEnumResponseResult
   applicationModel: ApplicationModel
   userStore: UserStore
   stageStore: StageStore
}

interface StoreApplicationToApplicationModelProps {
   applicationId: string
   applicationTemplateId: string
   applicationStore: ApplicationStore
}

export const storeApplicationAssigneeInUserStore = (
   transformedFieldResponse: any,
   userStore: UserStore,
   fieldResponse: FieldResponse
) => {
   const isFieldApplicationAssigneeField =
      fieldResponse.systemFieldEnum === SystemFieldEnum.ApplicationAssignee
   if (isFieldApplicationAssigneeField) {
      userStore.createOrUpdateUser({
         ...transformedFieldResponse.response
      })
   }
}

export const convertGqlApplicationTypeToTemplateApplicationType = (
   applicationType: ApplicationTypeEnum
): TemplateApplicationTypeEnum => {
   switch (applicationType) {
      case ApplicationTypeEnum.Tdr:
         return TemplateApplicationTypeEnum.Tdr
      case ApplicationTypeEnum.Default:
      default:
         return TemplateApplicationTypeEnum.Default
   }
}

export const setFieldResponseInApplicationModel = (
   field: UnionFieldModelType,
   applicationModel: ApplicationModel,
   stageStore: StageStore,
   fieldResponse: FieldResponse,
   userStore: UserStore,
   gofResponseId?: string
) => {
   if (!field.gof) throw new Error('Gof is not found for field')

   const transformedFieldResponse = convertGraphqlFieldResponseToUnionFieldResponse(
      field,
      fieldResponse
   )

   const gofId = field.gof.gofId
   const dummyGofResponseId = RecordUtils.getGoFResponseId(
      applicationModel.applicationId,
      gofId
   )

   const isApplicationStage =
      field.systemFieldEnum === SystemFieldEnum.ApplicationStage ||
      field.systemFieldEnum === SystemFieldEnum.ApplicationDisplayStage
   if (isApplicationStage)
      stageStore.createOrUpdateStage({
         ...(transformedFieldResponse.response as Stage)
      })

   applicationModel.createOrUpdateFieldResponse(
      gofId,
      gofResponseId || dummyGofResponseId,
      transformedFieldResponse
   )

   storeApplicationAssigneeInUserStore(
      transformedFieldResponse,
      userStore,
      fieldResponse
   )
}

const storeApplicationStageInStageStore = (
   transformedFieldResponse: any,
   stageStore: StageStore,
   fieldResponse: FieldResponse
) => {
   const isApplicationStageField =
      fieldResponse.systemFieldEnum === SystemFieldEnum.ApplicationStage

   if (isApplicationStageField) {
      stageStore.createOrUpdateStage({
         ...transformedFieldResponse.response
      })
   }
}

export const storeApplicationFieldResponsesInApplicationModel = (
   props: StoreApplicationFieldResponsesInApplicationModelProps
) => {
   const { fieldResponse, applicationModel, userStore, stageStore } = props

   const isFieldResponseType = fieldResponse.__typename === 'FieldResponse'
   if (!isFieldResponseType) return

   const fieldModel = applicationModel.applicationTemplate.fields.find(
      field => field.fieldId === fieldResponse.fieldId
   )

   if (!fieldModel?.gof) {
      throw new Error(`${fieldModel?.fieldId}: GOF is undefined in the Field`)
   }

   const gofId = fieldModel.gof.gofId
   const entityId = applicationModel.applicationId
   const gofResponseId = RecordUtils.getGoFResponseId(entityId, gofId)

   const transformedFieldResponse = convertGraphqlFieldResponseToUnionFieldResponse(
      fieldModel,
      fieldResponse
   )

   storeApplicationAssigneeInUserStore(
      transformedFieldResponse,
      userStore,
      fieldResponse
   )

   storeApplicationStageInStageStore(
      transformedFieldResponse,
      stageStore,
      fieldResponse
   )

   applicationModel.createOrUpdateFieldResponse(
      gofId,
      gofResponseId,
      transformedFieldResponse
   )
}

export const storeApplicationInApplicationStore = (
   props: StoreApplicationToApplicationModelProps
): ApplicationModel => {
   const { applicationId, applicationStore, applicationTemplateId } = props

   const application = {
      applicationId,
      recordId: applicationId,
      templateId: applicationTemplateId,
      gofResponses: []
   }

   applicationStore.createOrUpdateApplication(application)
   return applicationStore.getApplication(applicationId)
}

export const convertApplicationTemplateModelInputType = (
   applicationTemplate: Common_ApplicationTemplate_FragmentFragment
): ApplicationTemplateType => {
   const {
      pipelineItemTemplateId,
      gofs,
      assigneeFieldsMapping,
      applicationTemplateProperties: properties,
      applicationType
   } = applicationTemplate

   return {
      templateId: pipelineItemTemplateId,
      name: applicationTemplate.name,
      gofs: gofs.map(convertToGoFWithFieldsType),
      assigneeFieldsMapping: assigneeFieldsMapping,
      applicationTemplateProperties: {
         pluralName: properties?.pluralName ?? '',
         primaryDisplayNameFieldId: properties?.primaryDisplayNameFieldId ?? '',
         singularName: properties?.singularName ?? ''
      },
      workflows: [],
      slaFieldIds: applicationTemplate.slaFieldIds,
      templateApplicationType: convertGqlApplicationTypeToTemplateApplicationType(
         applicationType
      )
   }
}

export const storeApplicationTemplateModelInTemplateStore = (
   applicationTemplate: Common_ApplicationTemplate_FragmentFragment,
   applicationTemplateStore: ApplicationTemplateStore
) => {
   const templateId = applicationTemplate.pipelineItemTemplateId
   const applicationTemplateModelInput = convertApplicationTemplateModelInputType(
      applicationTemplate
   )

   applicationTemplateStore.createOrUpdateTemplate(
      applicationTemplateModelInput
   )

   const applicationTemplateModel = applicationTemplateStore.getTemplate(
      templateId
   )
   applicationTemplateModel.setTemplateDetailsStatus(
      TemplateDetailsStatusEnum.FetchedCompleteDetails
   )

   applicationTemplateModel.setCanMergeRecords(
      applicationTemplate.canMergeRecords
   )

   applicationTemplateModel.setDeemedApprovalFieldsConfig(
      applicationTemplate.deemedApprovalFields ?? null
   )
}

export const storeAddAnotherGofResponsesInApplicationModel = (
   addAnotherGofResponses: AddAnotherGoFResult[],
   applicationModel: ApplicationModel,
   fieldsStore: FieldsStore
): void =>
   addAnotherGofResponses.forEach(gofResponseConfig => {
      gofResponseConfig.gofResponses.forEach(gofResponse => {
         const gofResponseData: GOFResponse = {
            gofId: gofResponse.gofId,
            gofResponseId: gofResponse.gofResponseId
         }

         applicationModel.createOrUpdateGOFResponse(gofResponseData)

         gofResponse.fieldResponses.forEach(fieldResponse => {
            const fieldModel = fieldsStore.getField(fieldResponse.fieldId)

            const transformedFieldResponse = getTransformedFieldResponse(
               fieldModel,
               fieldResponse as FieldResponse
            )

            applicationModel.createOrUpdateFieldResponse(
               gofResponse.gofId,
               gofResponse.gofResponseId,
               transformedFieldResponse
            )
         })
      })
   })

export const storeApplicationBaseDetailsInTemplateStore = (
   templateId: string,
   templateName: string,
   applicationTemplateStore: ApplicationTemplateStore,
   applicationType: ApplicationTypeEnum,
   properties?: ApplicationTemplateProperties
): void => {
   applicationTemplateStore.createOrUpdateTemplate({
      name: templateName,
      templateId,
      applicationTemplateProperties: {
         pluralName: properties?.pluralName ?? '',
         primaryDisplayNameFieldId: properties?.primaryDisplayNameFieldId ?? '',
         singularName: properties?.singularName ?? ''
      },
      assigneeFieldsMapping: [],
      gofs: [],
      workflows: [],
      templateApplicationType: convertGqlApplicationTypeToTemplateApplicationType(
         applicationType
      )
   })
}

export const setApplicationTemplateSurveyNumberFieldConfig = (
   surveyNumberFieldConfig: SurveyNumberFieldConfig_FragmentFragment,
   template: ApplicationTemplateModel
) => {
   const {
      surveyNumberFieldId,
      prohibitingSupportingDocumentFieldId,
      areaFieldId,
      surveyNumberCategoryFieldId,
      gofId
   } = surveyNumberFieldConfig

   if (surveyNumberFieldId) {
      template.setSurveyNumberFieldId(surveyNumberFieldId)
   }

   if (surveyNumberCategoryFieldId) {
      template.setSurveyNumberCategoryFieldId(surveyNumberCategoryFieldId)
   }

   if (prohibitingSupportingDocumentFieldId) {
      template.setSurveyNoProhibitedDocumentFieldId(
         prohibitingSupportingDocumentFieldId
      )
   }

   if (areaFieldId) {
      template.setSurveyNumberAreaFieldId(areaFieldId)
   }

   if (gofId) {
      template.setSurveyNumberGofId(gofId)
   }

   const surveyNumberParentFieldIds: Array<string> = [
      surveyNumberFieldConfig.districtFieldId,
      surveyNumberFieldConfig.mandalFieldId,
      surveyNumberFieldConfig.ulbCodeFieldId,
      surveyNumberFieldConfig.villageFieldId
   ]

   template.setSurveyNumberParentFieldIds(surveyNumberParentFieldIds)
}

export const setApplicationTemplateShortfallFieldsConfig = (
   shortfallFieldsConfig: ShortFallFieldsConfig,
   template: ApplicationTemplateModel
): void => {
   const { fieldIds, gofIds } = shortfallFieldsConfig

   const safeFieldIds = (fieldIds ?? []).filter(Boolean) as Array<string>

   const safeGofIds = (gofIds ?? []).filter(Boolean) as Array<string>

   template.setShortfallFieldIds(safeFieldIds, safeGofIds)
}
